export interface StorageOptions {
  expiresIn?: number; // milliseconds
}

export interface StorageItem<T = any> {
  value: T;
  expires?: number;
  version?: string;
}

const storagePrefix = 'bz_';
const STORAGE_VERSION = '1.0.0'; // Change this to clear all localStorage data
const VERSION_KEY = '__storage_version__';

class LocalStorageService {
  constructor() {
    this.checkVersion();
  }

  private isAvailable(): boolean {
    try {
      return 'localStorage' in globalThis && globalThis.localStorage != null;
    } catch {
      return false;
    }
  }

  private getKey(key: string): string {
    return `${storagePrefix}${key}`;
  }

  private checkVersion(): void {
    if (!this.isAvailable()) return;

    try {
      const storedVersion = globalThis.localStorage.getItem(
        this.getKey(VERSION_KEY)
      );

      if (storedVersion !== STORAGE_VERSION) {
        // Version mismatch, clear all prefixed localStorage data
        this.clear();
        globalThis.localStorage.setItem(
          this.getKey(VERSION_KEY),
          STORAGE_VERSION
        );
      }
    } catch {
      // If we can't check version, clear everything to be safe
      this.clear();
    }
  }

  getItem<T = any>(key: string): T | null {
    if (!this.isAvailable()) return null;

    try {
      const item = globalThis.localStorage.getItem(this.getKey(key));
      if (!item) return null;

      const parsedItem = JSON.parse(item) as StorageItem<T>;

      // Check version compatibility
      if (parsedItem.version && parsedItem.version !== STORAGE_VERSION) {
        this.removeItem(key);
        return null;
      }

      // Check if item has expired
      if (parsedItem.expires && Date.now() > parsedItem.expires) {
        this.removeItem(key);
        return null;
      }

      return parsedItem.value;
    } catch {
      return null;
    }
  }

  setItem<T = any>(key: string, value: T, options?: StorageOptions): boolean {
    if (!this.isAvailable()) return false;

    try {
      const item: StorageItem<T> = {
        value,
        version: STORAGE_VERSION,
        ...(options?.expiresIn && {
          expires: Date.now() + options.expiresIn,
        }),
      };

      globalThis.localStorage.setItem(this.getKey(key), JSON.stringify(item));
      return true;
    } catch {
      return false;
    }
  }

  removeItem(key: string): boolean {
    if (!this.isAvailable()) return false;

    try {
      globalThis.localStorage.removeItem(this.getKey(key));
      return true;
    } catch {
      return false;
    }
  }

  clear(): boolean {
    if (!this.isAvailable()) return false;

    try {
      const keys = Object.keys(globalThis.localStorage).filter(key =>
        key.startsWith(storagePrefix)
      );
      for (const key of keys) {
        globalThis.localStorage.removeItem(key);
      }
      return true;
    } catch {
      return false;
    }
  }

  has(key: string): boolean {
    return this.getItem(key) !== null;
  }

  size(): number {
    if (!this.isAvailable()) return 0;

    try {
      return Object.keys(globalThis.localStorage).filter(key =>
        key.startsWith(storagePrefix)
      ).length;
    } catch {
      return 0;
    }
  }

  keys(): string[] {
    if (!this.isAvailable()) return [];

    try {
      return Object.keys(globalThis.localStorage)
        .filter(key => key.startsWith(storagePrefix))
        .map(key => key.replace(storagePrefix, ''));
    } catch {
      return [];
    }
  }

  getWithDefault<T>(key: string, defaultValue: T): T {
    const value = this.getItem<T>(key);
    return value ?? defaultValue;
  }

  updateItem<T>(
    key: string,
    updater: (current: T | null) => T,
    options?: StorageOptions
  ): boolean {
    const current = this.getItem<T>(key);
    const updated = updater(current);
    return this.setItem(key, updated, options);
  }

  clearExpired(): number {
    if (!this.isAvailable()) return 0;

    let clearedCount = 0;
    const now = Date.now();

    try {
      const keys = Object.keys(globalThis.localStorage).filter(
        key => key.startsWith(storagePrefix) && !key.endsWith(VERSION_KEY)
      );

      for (const key of keys) {
        try {
          const item = globalThis.localStorage.getItem(key);
          if (item) {
            const parsedItem = JSON.parse(item) as StorageItem;

            // Remove items with wrong version or expired items
            if (
              (parsedItem.version && parsedItem.version !== STORAGE_VERSION) ||
              (parsedItem.expires && now > parsedItem.expires)
            ) {
              globalThis.localStorage.removeItem(key);
              clearedCount++;
            }
          }
        } catch {
          // Remove corrupted items
          globalThis.localStorage.removeItem(key);
          clearedCount++;
        }
      }
    } catch {
      // Ignore errors
    }

    return clearedCount;
  }

  getVersion(): string {
    return STORAGE_VERSION;
  }

  migrate(
    migrationFn: (oldData: Record<string, any>) => Record<string, any>,
    newVersion: string
  ): boolean {
    if (!this.isAvailable()) return false;

    try {
      // Get all existing data
      const oldData: Record<string, any> = {};
      const keys = this.keys();

      for (const key of keys) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const value = this.getItem(key);
        if (value !== null) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          oldData[key] = value;
        }
      }

      // Run migration
      const newData = migrationFn(oldData);

      // Clear old data
      this.clear();

      // Update version
      globalThis.localStorage.setItem(this.getKey(VERSION_KEY), newVersion);

      // Set new data
      for (const [key, value] of Object.entries(newData)) {
        this.setItem(key, value);
      }

      return true;
    } catch {
      return false;
    }
  }
}

const localStorage = new LocalStorageService();

export default localStorage;
